import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation } from 'react-i18next';
import { useDropzone } from 'react-dropzone';
import { CloudArrowUpIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useUIStore } from '../../stores/uiStore';
import { useFileStore } from '../../stores/fileStore';
import { useAuthStore } from '../../stores/authStore';
import { filesApi } from '../../api/files';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { useToast } from '../ui/Toaster';
import { formatBytes } from '../../utils/dateFormat';

interface UploadItem {
  id: string;
  file: File;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'error';
  error?: string;
}

export function UploadModal() {
  const { t } = useTranslation('files');
  const [uploads, setUploads] = useState<UploadItem[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  const isOpen = useUIStore((s) => s.isUploadModalOpen);
  const closeModal = useUIStore((s) => s.closeUploadModal);
  const pendingUploadFiles = useUIStore((s) => s.pendingUploadFiles);
  const clearPendingUploadFiles = useUIStore((s) => s.clearPendingUploadFiles);
  const currentFolderId = useFileStore((s) => s.currentFolderId);
  const addFile = useFileStore((s) => s.addFile);
  const updateStorage = useAuthStore((s) => s.updateStorage);
  const toast = useToast();

  // Add files from drag and drop when modal opens
  useEffect(() => {
    if (isOpen && pendingUploadFiles.length > 0) {
      const newUploads = pendingUploadFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        progress: 0,
        status: 'pending' as const,
      }));
      setUploads((prev) => [...prev, ...newUploads]);
      clearPendingUploadFiles();
    }
  }, [isOpen, pendingUploadFiles, clearPendingUploadFiles]);

  const onDrop = useCallback((acceptedFiles: File[]) => {
    const newUploads = acceptedFiles.map((file) => ({
      id: crypto.randomUUID(),
      file,
      progress: 0,
      status: 'pending' as const,
    }));
    setUploads((prev) => [...prev, ...newUploads]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    disabled: isUploading,
  });

  const uploadFile = async (upload: UploadItem) => {
    setUploads((prev) =>
      prev.map((u) => (u.id === upload.id ? { ...u, status: 'uploading' } : u))
    );

    try {
      // Get presigned URL
      const { uploadUrl, fileId } = await filesApi.getUploadUrl({
        fileName: upload.file.name,
        mimeType: upload.file.type || 'application/octet-stream',
        size: upload.file.size,
        folderId: currentFolderId,
      });

      // Upload to R2
      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable) {
            const progress = Math.round((e.loaded / e.total) * 100);
            setUploads((prev) =>
              prev.map((u) => (u.id === upload.id ? { ...u, progress } : u))
            );
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => reject(new Error('Upload failed')));

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', upload.file.type || 'application/octet-stream');
        xhr.send(upload.file);
      });

      // Confirm upload
      const { file, ownerStorageUsed } = await filesApi.confirmUpload(fileId);
      console.log('Upload confirmed, file received:', JSON.stringify(file, null, 2));

      addFile(file);
      updateStorage(ownerStorageUsed);

      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id ? { ...u, status: 'completed', progress: 100 } : u
        )
      );
    } catch (error) {
      setUploads((prev) =>
        prev.map((u) =>
          u.id === upload.id
            ? { ...u, status: 'error', error: 'Upload failed' }
            : u
        )
      );
    }
  };

  const handleUpload = async () => {
    const pendingUploads = uploads.filter((u) => u.status === 'pending');
    if (pendingUploads.length === 0) return;

    setIsUploading(true);

    let succeeded = 0;
    let failed = 0;
    for (const upload of pendingUploads) {
      await uploadFile(upload);
      // Read latest status from state via callback
      setUploads((prev) => {
        const item = prev.find((u) => u.id === upload.id);
        if (item?.status === 'completed') succeeded++;
        else if (item?.status === 'error') failed++;
        return prev;
      });
    }

    setIsUploading(false);

    if (failed === 0) {
      toast.success(t('upload.complete'), t('upload.filesUploaded', { count: succeeded }));
    } else if (succeeded > 0) {
      toast.warning(
        t('upload.partialComplete', 'Upload partially completed'),
        t('upload.partialResult', '{{succeeded}} uploaded, {{failed}} failed', { succeeded, failed })
      );
    } else {
      toast.error(t('upload.failed', 'Upload failed'), t('upload.allFailed', 'All files failed to upload'));
    }
  };

  const removeUpload = (id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id));
  };

  const handleClose = () => {
    if (isUploading) return;
    setUploads([]);
    closeModal();
  };

  const pendingCount = uploads.filter((u) => u.status === 'pending').length;
  const completedCount = uploads.filter((u) => u.status === 'completed').length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('upload.title')}
      size="lg"
    >
      <div className="space-y-4">
        {/* Drop zone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all duration-150 ${
            isDragActive
              ? 'scale-[1.02] border-primary-500 bg-primary-50 dark:bg-primary-900/30'
              : 'border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-800'
          }`}
        >
          <input {...getInputProps()} />

          <div className={`transition-transform ${isDragActive ? '-translate-y-1' : ''}`}>
            <CloudArrowUpIcon className="w-12 h-12 mx-auto text-gray-400 dark:text-slate-500 mb-4" />

            {isDragActive ? (
              <p className="text-primary-600 dark:text-primary-400 font-medium">{t('upload.dropHere')}</p>
            ) : (
              <>
                <p className="text-gray-900 dark:text-white font-medium">
                  {t('upload.dragDrop')}
                </p>
                <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
                  {t('upload.orClick')}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Upload list */}
        <AnimatePresence>
          {uploads.length > 0 && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="max-h-64 overflow-y-auto space-y-2"
            >
              {uploads.map((upload) => (
                <motion.div
                  key={upload.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="bg-gray-50 dark:bg-slate-700 rounded-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-gray-900 dark:text-white truncate">{upload.file.name}</p>
                      <p className="text-xs text-gray-500 dark:text-slate-400">
                        {formatBytes(upload.file.size)}
                      </p>
                    </div>

                    {upload.status === 'completed' ? (
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                    ) : upload.status !== 'uploading' ? (
                      <button
                        onClick={() => removeUpload(upload.id)}
                        className="p-1 hover:bg-gray-200 dark:hover:bg-slate-600 rounded"
                      >
                        <XMarkIcon className="w-4 h-4 text-gray-400 dark:text-slate-400" />
                      </button>
                    ) : null}
                  </div>

                  <ProgressBar progress={upload.progress} status={upload.status} />

                  {upload.error && (
                    <p className="text-xs text-red-500 mt-1">{upload.error}</p>
                  )}
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Actions */}
        <div className="flex items-center justify-between pt-2">
          <p className="text-sm text-gray-500 dark:text-slate-400">
            {uploads.length > 0 && (
              <>
                {t('upload.progress', { completed: completedCount, total: uploads.length })}
              </>
            )}
          </p>

          <div className="flex gap-3">
            <Button variant="secondary" onClick={handleClose} disabled={isUploading}>
              {completedCount === uploads.length && uploads.length > 0 ? t('common:buttons.done') : t('common:buttons.cancel')}
            </Button>
            {pendingCount > 0 && (
              <Button onClick={handleUpload} isLoading={isUploading}>
                {t('upload.uploadCount', { count: pendingCount })}
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
